import { Lightbulb, TrendingUp, Users, Target } from "lucide-react";
import innovationImage from "@/assets/innovation-network.jpg";
import designThinkingImage from "@/assets/design-thinking.jpg";

interface ServicesProps {
  language: 'en' | 'de';
}

export const Services = ({ language }: ServicesProps) => {
  const content = {
    en: {
      title: "Strategic Services",
      subtitle: "Proven Methodologies for Measurable Impact",
      services: [
        {
          icon: Lightbulb,
          title: "Innovation & Trend Scouting",
          description: "Stay ahead of market shifts with comprehensive technology and business trend analysis across your industry."
        },
        {
          icon: Users,
          title: "Design Thinking Workshops",
          description: "Facilitate breakthrough innovations through structured ideation sessions that deliver actionable value track records."
        },
        {
          icon: TrendingUp,
          title: "Strategic Advisory",
          description: "Navigate complex IT and business transformations with strategic insights from a seasoned C-level perspective."
        },
        {
          icon: Target,
          title: "Independent Sparring Partner",
          description: "Challenge assumptions and validate strategies with an experienced advisor who understands executive decision-making."
        }
      ],
      industries: {
        title: "Industry Expertise",
        list: ["Automotive", "Information Technology", "SAP", "Manufacturing", "Software", "AI", "Development", "Business Models", "Building & Construction", "Telecommunications", "Public Sector", "Startups & Scale-ups"]
      }
    },
    de: {
      title: "Strategische Dienstleistungen",
      subtitle: "Bewährte Methoden für messbare Wirkung",
      services: [
        {
          icon: Lightbulb,
          title: "Innovation & Trend Scouting",
          description: "Bleiben Sie Marktveränderungen voraus mit umfassender Technologie- und Geschäftstrendanalyse in Ihrer Branche."
        },
        {
          icon: Users,
          title: "Design Thinking Workshops",
          description: "Ermöglichen Sie bahnbrechende Innovationen durch strukturierte Ideenfindungssitzungen mit handfesten Erfolgsbilanzen."
        },
        {
          icon: TrendingUp,
          title: "Strategische Beratung",
          description: "Navigieren Sie komplexe IT- und Geschäftstransformationen mit strategischen Einsichten aus C-Level-Perspektive."
        },
        {
          icon: Target,
          title: "Unabhängiger Sparringspartner",
          description: "Hinterfragen Sie Annahmen und validieren Sie Strategien mit einem erfahrenen Berater, der Executive-Entscheidungen versteht."
        }
      ],
      industries: {
        title: "Branchenexpertise",
        list: ["Automotive", "Informationstechnologie", "SAP", "Fertigung", "Software", "KI", "Entwicklung", "Geschäftsmodelle", "Bau & Konstruktion", "Telekommunikation", "Öffentlicher Sektor", "Startups & Scale-ups"]
      }
    }
  };

  const t = content[language];

  return (
    <section id="strategy" className="py-20 lg:py-32 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="mb-4 text-foreground">{t.title}</h2>
          <p className="text-xl text-muted-foreground">{t.subtitle}</p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {t.services.map((service, index) => {
            const Icon = service.icon;
            const anchorIds = ['innovation', 'design-thinking', 'AI', 'sparring-partner'];
            return (
              <div 
                key={index}
                id={anchorIds[index]}
                className="bg-card rounded-xl p-6 shadow-card hover:shadow-elegant transition-smooth border border-border hover:border-accent/50 group"
              >
                <div className="w-12 h-12 rounded-lg gradient-accent flex items-center justify-center mb-4 group-hover:scale-110 transition-bounce shadow-glow">
                  <Icon className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-card-foreground">{service.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{service.description}</p>
              </div>
            );
          })}
        </div>

        {/* Visual Showcase */}
        <div id="facilitator" className="grid md:grid-cols-2 gap-8 mb-16">
          <div className="relative rounded-2xl overflow-hidden shadow-elegant group">
            <img 
              src={designThinkingImage} 
              alt="Design thinking workshop" 
              className="w-full h-80 object-cover group-hover:scale-105 transition-smooth"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/90 to-transparent flex items-end p-6">
              <h3 className="text-2xl font-semibold text-primary-foreground">
                {language === 'en' ? 'Collaborative Innovation' : 'Kollaborative Innovation'}
              </h3>
            </div>
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-elegant group">
            <img 
              src={innovationImage} 
              alt="Technology innovation network" 
              className="w-full h-80 object-cover group-hover:scale-105 transition-smooth"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/90 to-transparent flex items-end p-6">
              <h3 className="text-2xl font-semibold text-primary-foreground">
                {language === 'en' ? 'Technology Leadership' : 'Technologie-Führung'}
              </h3>
            </div>
          </div>
        </div>

        {/* Industries */}
        <div id="expertise" className="bg-muted/50 rounded-2xl p-8 lg:p-12 border border-border">
          <h3 className="text-2xl font-semibold mb-6 text-center text-foreground">{t.industries.title}</h3>
          <div className="flex flex-wrap justify-center gap-4">
            {t.industries.list.map((industry, index) => (
              <div 
                key={index}
                className="bg-background px-6 py-3 rounded-full border border-accent/30 text-foreground font-medium hover:border-accent hover:shadow-glow transition-smooth"
              >
                {industry}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};